import { Navigate } from 'react-router-dom';

import { isDesktop } from '../../app/desktop';
import { useSession } from '../../stores/session';
import { DashboardPage } from '../dashboard/DashboardPage';
import { LandingPage } from './LandingPage';

/**
 * The front door, and which one you get.
 *
 * ## Signed in: the dashboard, unchanged
 *
 * Somebody with an account does not need the site explained to them, and a
 * landing page in front of their own home would be an advertisement for a thing
 * they already bought. They land where they landed before.
 *
 * ## Signed out: an explanation
 *
 * This used to be the dashboard for everybody, on the argument that a site whose
 * proposition is "there is music here" should show the music rather than write a
 * paragraph claiming it. That argument was good and it was incomplete: the
 * dashboard answers *what is on* and never answers *what this is*. A stranger
 * looking at a wall of tracks by people they have not heard of can tell it is a
 * music site, and cannot tell that a live human being picks every record, that
 * anyone can host, that submitting is free, or that no amount of money outranks
 * the host's own pick. Those are the reasons to make an account, and none of
 * them are visible in a grid of artwork.
 *
 * So both, chosen by who is asking - rather than one page trying to be an
 * argument and a dashboard at once and being a poor version of each.
 *
 * ## Not a redirect
 *
 * Both live at `/`. Sending a stranger to `/welcome` would put a URL in front of
 * the site that gets shared, indexed and linked instead of the home page, and it
 * would mean signing in has to bounce you somewhere else afterwards.
 *
 * ## Except in the desktop shell, where it is one
 *
 * Somebody who downloaded, installed and opened an application has been sold
 * already. The landing page is an argument for a thing they have bought, and it
 * is the one audience for whom "what is this" is a wasted screen - so the front
 * door there is the sign-in.
 *
 * A redirect rather than a render, and the reason is the paragraph above: every
 * argument against one is about URLs being shared, indexed and linked, and the
 * shell has no address bar, is not crawled, and shares nothing. What is left is
 * a redirect's advantages - `/login` is already a bare screen with no header
 * over it, so it needs no special casing anywhere else.
 *
 * `replace`, so the back button does not walk into the redirect it just came
 * out of.
 *
 * Only the front door moves. A deep link into a track, a profile or a room
 * still renders for a signed-out desktop reader, because those are the links
 * people send each other and a login wall in front of one is a dead end rather
 * than a welcome.
 */
export function HomePage() {
  const user = useSession((state) => state.user);

  if (user) return <DashboardPage />;

  if (isDesktop) return <Navigate to="/login" replace />;

  return <LandingPage />;
}
